import { IApprovalDetail, IApprovalProccessId } from "@/types/approval.type"

const normalizeApprovalIdentifier = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null
  }

  const normalizedValue = String(value).trim()
  return normalizedValue ? normalizedValue : null
}

const normalizeApprovalIdentifierList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map(normalizeApprovalIdentifier)
      .filter((item): item is string => typeof item === "string" && item.length > 0)
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map(normalizeApprovalIdentifier)
      .filter((item): item is string => typeof item === "string" && item.length > 0)
  }

  return []
}

const getApprovalDetailActorIds = (detail: IApprovalDetail | null | undefined): string[] => {
  return [
    normalizeApprovalIdentifier(detail?.approver?.employee_id),
    normalizeApprovalIdentifier(detail?.approver?.nik),
    normalizeApprovalIdentifier(detail?.approver_id),
  ].filter((item): item is string => typeof item === "string" && item.length > 0)
}

const matchesApprovalIdentifier = (detail: IApprovalDetail | null | undefined, employeeId: string | null) => {
  if (!employeeId) {
    return false
  }

  return getApprovalDetailActorIds(detail).includes(employeeId)
}

interface IApprovalActorAccess {
  approvalDetailId: string | null
  canApprove: boolean
  isApprover: boolean
  isDeputy: boolean
  isPic: boolean
}

export const getApprovalActorAccess = (
  approvalProcess: IApprovalProccessId | null | undefined,
  employeeId: string | null | undefined,
): IApprovalActorAccess => {
  const normalizedEmployeeId = normalizeApprovalIdentifier(employeeId)

  if (!approvalProcess || !normalizedEmployeeId) {
    return {
      approvalDetailId: null,
      canApprove: false,
      isApprover: false,
      isDeputy: false,
      isPic: false,
    }
  }

  const detailList = Array.isArray(approvalProcess.detail) ? approvalProcess.detail : []
  const formSetting = approvalProcess.form_id?.form_setting?.[0] || {}
  const picEmployeeIds = normalizeApprovalIdentifierList(formSetting?.pic)
  const picApprovalRefer = normalizeApprovalIdentifier(formSetting?.pic_approval_refer)

  const isApprover = detailList.some((detail) => matchesApprovalIdentifier(detail, normalizedEmployeeId))
  const isDeputy = detailList.some(
    (detail) => normalizeApprovalIdentifier(detail?.deputy_approver) === normalizedEmployeeId,
  )
  const isPic =
    picEmployeeIds.includes(normalizedEmployeeId) &&
    !!picApprovalRefer &&
    detailList.some((detail) => matchesApprovalIdentifier(detail, picApprovalRefer))

  const pendingApproverDetail = detailList.find(
    (detail) => detail?.status === false && matchesApprovalIdentifier(detail, normalizedEmployeeId),
  )
  const pendingDeputyDetail = detailList.find(
    (detail) =>
      detail?.status === false && normalizeApprovalIdentifier(detail?.deputy_approver) === normalizedEmployeeId,
  )
  const pendingPicDetail =
    picEmployeeIds.includes(normalizedEmployeeId) && !!picApprovalRefer
      ? detailList.find((detail) => detail?.status === false && matchesApprovalIdentifier(detail, picApprovalRefer))
      : null

  const activeApprovalDetail = pendingApproverDetail || pendingDeputyDetail || pendingPicDetail || null

  return {
    approvalDetailId: activeApprovalDetail?._id || null,
    canApprove: !!activeApprovalDetail,
    isApprover,
    isDeputy,
    isPic,
  }
}
